import qs from 'qs'
import request from '@/utils/request'
import { ComboItem } from './types'

export const COMBOS_URL = '/api/v1/combos'

/**
 * `comboCodes` 는 다건이라 `key=a&key=b` 로 편다 — BE 가 `List<String>` 으로 받는다.
 * 공용 `Utils.qsStringify` 는 `arrayFormat: 'comma'` 라 이 계약에 맞지 않는다
 * (returnReceipt/apis.ts, rgr11V2/queries/apis.ts 선례).
 */
const toQuery = (comboCodes: string[]): string => qs.stringify({ comboCodes }, { arrayFormat: 'repeat', skipNulls: true })

/** 화면 콤보 메타 조회 — GET /api/v1/combos?comboCodes=... (LOGISPRD-12385). */
export const getCombos = async (comboCodes: string[]) =>
  await request.get<ApiResponseV2<{ items: ComboItem[] }>>(`${COMBOS_URL}?${toQuery(comboCodes)}`)

/**
 * `getCombos` 응답을 1.0 `/api/common/combo` 모양 `{CODE:{COMBO_OPT_CD[],COMBO_OPT_NM[]}}` 으로 바꾼다 (LOGISPRD-14161).
 * 1.0 모양을 읽는 기존 소비처를 고치지 않고 호출만 2.0 으로 옮기기 위한 변환이다.
 * 미등록 코드는 BE 가 빈 options 로 돌려준다 — 응답에 코드가 빠져 있어도 빈 배열로 채운다. name 이 null 이면 '' (소비처가 `NM[idx] || code` 로 코드 대체).
 * 옵션 순서는 2.0 순서(COMBO_OPT_CD 정렬)다 — 1.0 은 ORDER BY 없이 id 순이었다.
 */
export const getLegacyShapedCombos = async <C extends string>(
  comboCodes: C[],
): Promise<Record<C, { COMBO_OPT_CD: string[]; COMBO_OPT_NM: string[] }>> => {
  const res = await getCombos(comboCodes)
  const byCode = new Map((res.data?.items ?? []).map((item) => [item.comboCode, item.options]))
  return Object.fromEntries(
    comboCodes.map((code) => {
      const options = byCode.get(code) ?? []
      return [code, { COMBO_OPT_CD: options.map((o) => o.code), COMBO_OPT_NM: options.map((o) => o.name ?? '') }]
    }),
  ) as Record<C, { COMBO_OPT_CD: string[]; COMBO_OPT_NM: string[] }>
}
